import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';

// ---------- 공용 검색 헬퍼 (SearchPage + CommandPalette 공유) ----------
// RPC(workspace_search_pages, 마이그레이션 0021) 존재 여부 모듈 캐시.
// 'unknown' → 첫 검색에서 RPC 시도 / 'ok' → 계속 사용 / 'missing' → 제목 ilike 폴백 고정.
let rpcState = 'unknown';

// 마이그레이션 0021 미적용(RPC 미존재) 여부 — 안내 배너 표시용.
export function isBodySearchMissing() {
  return rpcState === 'missing';
}

/**
 * 페이지 검색. RPC 가 있으면 제목+본문 전문검색(mode:'fts', snippet 포함),
 * 없으면 기존 제목 ilike 폴백(mode:'title'). 빈 검색어는 최근 수정순 목록.
 * 반환: { mode, rows: [{ id, title, icon, type, snippet?, updated_at }] }
 */
export async function searchPages(orgId, term) {
  const t = (term || '').trim();
  if (t && rpcState !== 'missing') {
    try {
      const { data, error } = await supabase.rpc('workspace_search_pages', { q: t });
      if (!error) {
        rpcState = 'ok';
        return {
          mode: 'fts',
          rows: (data || []).map((r) => ({
            id: r.page_id,
            title: r.title,
            icon: r.icon,
            type: r.type,
            snippet: r.snippet,
            updated_at: r.updated_at,
          })),
        };
      }
      // 함수 미존재(마이그레이션 0021 미적용) → 폴백 고정. 그 외 오류는 이번만 폴백.
      if (error.code === 'PGRST202' || error.code === '42883'
        || /workspace_search_pages/i.test(error.message || '')) {
        rpcState = 'missing';
      }
    } catch (e) { /* 네트워크 등 — 아래 폴백 */ }
  }
  // 제목 ilike 폴백 (기존 동작 유지)
  if (!orgId) return { mode: 'title', rows: [] };
  try {
    let query = supabase
      .schema('workspace')
      .from('pages')
      .select('id, title, icon, type, updated_at')
      .eq('org_id', orgId)
      .eq('archived', false)
      .order('updated_at', { ascending: false })
      .limit(40);
    if (t) query = query.ilike('title', `%${t}%`);
    const { data } = await query;
    return { mode: 'title', rows: data || [] };
  } catch (e) {
    return { mode: 'title', rows: [] };
  }
}

// 스니펫의 [[매칭]] 마커를 <mark> 하이라이트로 렌더.
export function renderSnippet(snippet) {
  if (!snippet) return null;
  const parts = String(snippet).split(/\[\[(.*?)\]\]/g);
  return parts.map((p, i) => (
    i % 2 === 1
      ? <mark key={i} style={{ background: 'rgba(255, 212, 0, .4)', padding: 0, borderRadius: 2 }}>{p}</mark>
      : <span key={i}>{p}</span>
  ));
}

export default function SearchPage() {
  const { membership } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ftsMissing, setFtsMissing] = useState(false);

  useEffect(() => {
    if (!membership?.org_id) return undefined;
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      const { rows: result } = await searchPages(membership.org_id, q);
      if (active) {
        setRows(result);
        setFtsMissing(isBodySearchMissing());
        setLoading(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q, membership?.org_id]);

  return (
    <div className="page-narrow" style={{ maxWidth: 680, margin: '0 auto', padding: '40px 24px' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 14 }}>검색</h1>
      <input
        className="inp"
        autoFocus
        placeholder="페이지 제목·본문 검색…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ marginBottom: 16 }}
      />
      {ftsMissing && (
        <div
          className="muted"
          style={{
            fontSize: 12.5, padding: '8px 12px', marginBottom: 12,
            background: 'var(--side)', border: '1px solid var(--border)', borderRadius: 8,
          }}
        >
          본문 검색은 마이그레이션 0021 적용 후 사용 가능합니다. 현재는 제목 검색만 동작합니다.
        </div>
      )}
      {loading && <div className="muted">검색 중…</div>}
      {!loading && rows.length === 0 && <div className="muted">결과가 없습니다.</div>}
      {rows.map((p) => (
        <div
          key={p.id}
          onClick={() => navigate(`/page/${p.id}`)}
          style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 8px', borderRadius: 8, cursor: 'pointer' }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <span>{p.icon || (p.type === 'database' ? '🗂️' : '📄')}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 500 }}>{p.title || '제목 없음'}</div>
            {p.snippet && (
              <div
                className="muted"
                style={{ fontSize: 12.5, marginTop: 2, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
              >
                {renderSnippet(p.snippet)}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
